/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, Dimensions, TouchableOpacity } from 'react-native';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import ProductItem from '@components/global/ProductItem';
import PaymentTermsSearch from '@components/form/search/PaymentTermsSearch';
import CustomerClusterSearch from '@components/form/search/CustomerClusterSearch';
import CreateNewOption from '@/screens/setting/market-business-type/components/CreateNewOption';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import Table from '@components/table/Table';
import SearchTable from '@components/table/SearchTable';
import ScrollContainer from '@components/global/ScrollContainer';
import * as Layout from '@components/screen/Layout';
import { isWeb } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import {
  convertProductMarketType,
  isVn,
  exportCsvApi,
  hasNoAccess,
} from '@global/helpers';
import { SociollaSelectBox } from '@components/sociolla/form';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';

const { height: windowHeight } = Dimensions.get('window');
const tableStructure = {
  image: {
    head: 'Image',
    body: 'image',
    type: 'image',
  },
  product_name: {
    head: 'Product Name',
    body: 'product_name',
  },
  action: {
    head: '',
    type: 'action_icon',
    actions: [
      {
        name: 'delete',
      },
    ],
  },
};

const viewOnlyTableStructure = {
  image: tableStructure.image,
  product_name: tableStructure.product_name,
};

const limit = 10;

const MarketBusinessTypeScreen = (props) => {
  const dispatch = useDispatch();
  const isShowOption = useSelector((state) => state.modal.marketCreateOption);
  const isViewOnly = !hasNoAccess(['distributor_manager']);

  const [detailData, setDetailData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [products, setProducts] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [activeProductTab, setActiveProductTab] = useState('premium');

  const FormValidationSchema = Yup.object().shape({
    market_type: Yup.string().required('Market Type is required.'),
    business_type: Yup.string().required('Business Type is required.'),
  });

  const getMarketType = useApi(MsB2b.getMarketType);
  const getMarketTypeDetail = useApi(MsB2b.getMarketTypeDetail);

  const getMarketTypes = async () => {
    const response = await getMarketType.request();
    if (response?.data?.data?.length) {
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      setTableData(result);
    } else {
      setTableData([]);
    }
  };

  const getMarketTypeProduct = async (
    id,
    business_type_id,
    searchVal = '',
    newSkip = 0,
    isPremium = true,
  ) => {
    const isMore = newSkip > 0;
    try {
      setSkip(newSkip);
      if (searchVal && searchVal.trim() !== '') {
        setSearchQuery(searchVal);
        setIsSearch(true);
      } else {
        setSearchQuery('');
        setIsSearch(false);
      }
      if (isMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      const params = {
        filter: {
          is_premium: isPremium,
        },
        skip: newSkip,
        limit: limit,
      };
      if (searchVal) {
        params.filter.$or = [
          { 'product.name': { $regex: searchVal, $options: 'i' } },
          { 'brand.name': { $regex: searchVal, $options: 'i' } },
          { 'combination.reference': { $regex: searchVal, $options: 'i' } },
          { 'combination.ean_no': { $regex: searchVal, $options: 'i' } },
        ];
      }
      const response = await MsB2b.getProducts(id, business_type_id, {
        params: params,
      });
      if (response?.data?.data) {
        const result = response.data.data.map((item) => {
          item = convertProductMarketType(item);
          return item;
        });
        if (isMore) {
          const finalResult = products.concat(result);
          setProducts(finalResult);
        } else {
          setProducts(result);
        }
      } else {
        if (!isMore) {
          setProducts([]);
        }
      }
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    } catch (err) {
      if (isMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const getDetail = async (id) => {
    const response = await getMarketTypeDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      result.code = result.code.toUpperCase();
      if (props?.route?.params?.businessId) {
        const businessId = props.route.params.businessId;
        result.business_id = businessId;
        const findBusiness = result.business_type?.find(
          (a) => a._id == businessId,
        );
        result.business_code = findBusiness?.code ? findBusiness.code : '';
        result.business_description = findBusiness?.description
          ? findBusiness.description
          : '';
        result.customer = {};
        result.customer.payment_terms = findBusiness?.customer?.payment_terms
          ? findBusiness?.customer?.payment_terms
          : '';
        result.customer.limit_amount = findBusiness?.customer?.limit_amount
          ? findBusiness?.customer?.limit_amount.toString()
          : '';
        result.customer.cluster = findBusiness?.customer?.cluster
          ? findBusiness?.customer?.cluster
          : '';

        getMarketTypeProduct(result._id, businessId, '', 0, true);

        let isActiveBt = findBusiness?.is_active;
        if (isActiveBt == null || isActiveBt == undefined) {
          isActiveBt = true;
        }
        result.is_active = isActiveBt;
      } else {
        let isActive = result.is_active;
        if (isActive == null || isActive == undefined) {
          isActive = true;
        }
        result.is_active = isActive;
      }
      result.is_active = result.is_active
        ? { label: 'Active', value: true }
        : { label: 'In Active', value: false };
      setDetailData(result);
      getMarketTypes();
    }
  };

  const goBack = () => {
    props.navigation.replace('MarketBusinessTypeFormScreen', {});
    props.navigation.navigate('MarketBusinessTypeScreen');
  };

  const processMarketTypeProduct = async (data, is_premium = true) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const response = await MsB2b.actionMarketTypeProduct(
        detailData.id,
        detailData.business_id,
        {
          product_id: data.id,
          combination_id: data.product_combination_id,
          is_premium: is_premium,
          market_type: {
            id: detailData.id,
            code: detailData.code,
            business_type: {
              id: detailData.business_id,
              code: detailData.business_code,
            },
          },
        },
      );

      if (response?.data?.success) {
        Toast.show({
          text1: 'Successfully added product',
          type: 'success',
        });
        getMarketTypeProduct(
          detailData._id,
          detailData.business_id,
          '',
          0,
          is_premium,
        );
      } else {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: err?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const deleteMarketTypeProduct = async (data) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const response = await MsB2b.deleteBusinessTypeProduct(
        detailData.id,
        detailData.business_id,
        data.product.id,
        data.product_combination_id,
      );
      if (response?.data?.success == true) {
        const newProduct = JSON.parse(JSON.stringify(products));
        const idx = newProduct.findIndex(
          (item) => item.product.id == data.product.id,
        );
        if (idx > -1) {
          newProduct.splice(idx, 1);
          setProducts(newProduct);
          Toast.show({
            text1: 'Successfully deleted product',
            type: 'success',
          });
        }
      } else {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: err?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const onSelectProduct = (data, is_bulk = false) => {
    dispatch(showPopup({ name: 'marketCreateOption', value: false }));
    if (!is_bulk) {
      processMarketTypeProduct(data, activeProductTab === 'premium');
    } else {
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
  };

  const onRemoveProduct = (data) => {
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be deleted. Do you want to proceed?',
        onCallback: () => deleteMarketTypeProduct(data),
      }),
    );
  };

  const onMapAction = ({ data, index, action }) => {
    if (data) {
      if (action.name == 'delete') {
        onRemoveProduct(data);
      }
    }
  };

  const onExportAssortment = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await exportCsvApi(
        'product-assortment',
        {
          market_type_id: detailData.id,
          business_type_id: detailData.business_id,
        },
        {
          isDownload: false,
          message: 'Export in progress, the result will be sent to your email.',
        },
      );
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setDetailData({});
      setActiveProductTab('premium');
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container>
      {isEdit && getMarketTypeDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          initialValues={{
            id: isEdit && detailData?.id ? detailData.id : '',
            market_type: isEdit && detailData?.code ? detailData.code : '',
            business_type:
              isEdit && detailData?.business_code
                ? detailData.business_code
                : '',
            business_type_id:
              isEdit && detailData?.business_id ? detailData.business_id : '',
            business_type_description:
              isEdit && detailData?.business_description
                ? detailData.business_description
                : '',
            payment_terms:
              isEdit && detailData?.customer?.payment_terms
                ? detailData.customer.payment_terms
                : '',
            limit_amount:
              isEdit && detailData?.customer?.limit_amount
                ? detailData.customer.limit_amount
                : '',
            cluster:
              isEdit && detailData?.customer?.cluster
                ? detailData.customer.cluster
                : '',
            is_active: isEdit ? detailData?.is_active : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              const isViet = isVn();
              let isAdd = false;
              let finalPayload = {};
              const old_market_types = JSON.parse(JSON.stringify(tableData));
              const customer = {
                payment_terms: {
                  id: payload?.payment_terms?.id,
                  name: payload?.payment_terms?.name,
                },
                cluster: {
                  id: payload?.cluster?.id,
                  name: payload?.cluster?.name,
                },
                limit_amount: payload?.limit_amount || 0,
              };
              const is_active = payload.is_active.value;
              if (
                old_market_types.findIndex((ch) => ch.id == payload.id) > -1
              ) {
                const old_index = old_market_types.findIndex(
                  (ch) => ch.id == payload.id,
                );
                if (
                  old_market_types[old_index].business_type &&
                  old_market_types[old_index].business_type.length
                ) {
                  if (
                    old_market_types[old_index].business_type.findIndex(
                      (cb) => cb._id == payload.business_type_id,
                    ) > -1
                  ) {
                    const newBt = [
                      {
                        _id: payload.business_type_id,
                        code: payload.business_type,
                        description: payload.business_type_description,
                        ...(isViet
                          ? {
                              customer_vn: customer,
                              is_active_vn: is_active,
                            }
                          : {
                              customer,
                              is_active,
                            }),
                      },
                    ];
                    finalPayload = {
                      code: payload.market_type.toUpperCase(),
                      business_type: newBt,
                    };
                  } else {
                    const newBt = JSON.parse(
                      JSON.stringify(old_market_types[old_index].business_type),
                    );
                    newBt.push({
                      code: payload.business_type,
                      description: payload.business_type_description,
                      ...(isViet
                        ? {
                            customer_vn: customer,
                          }
                        : {
                            customer,
                          }),
                    });
                    finalPayload = {
                      code: payload.market_type.toUpperCase(),
                      business_type: newBt,
                    };
                  }
                } else {
                  finalPayload = {
                    code: payload.market_type.toUpperCase(),
                    business_type: [
                      {
                        code: payload.business_type,
                        description: payload.business_type_description,
                        ...(isViet
                          ? {
                              customer_vn: customer,
                              is_active_vn: is_active,
                            }
                          : {
                              customer,
                              is_active,
                            }),
                      },
                    ],
                  };
                }
              } else {
                isAdd = true;
                finalPayload = {
                  code: payload.market_type.toUpperCase(),
                  business_type: [
                    {
                      code: payload.business_type,
                      description: payload.business_type_description,
                      ...(isViet
                        ? {
                            customer_vn: customer,
                            is_active_vn: is_active,
                          }
                        : {
                            customer,
                            is_active,
                          }),
                    },
                  ],
                };
              }

              if (isAdd) {
                const createData = await MsB2b.createMarketType(finalPayload);
                if (createData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully added data',
                    type: 'success',
                  });
                  setIsEdit(true);
                  props.navigation.navigate('MarketBusinessTypeScreen');
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              } else {
                const updateData = await MsB2b.updateMarketType(
                  detailData._id,
                  finalPayload,
                );
                if (updateData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  props.navigation.navigate('MarketBusinessTypeScreen');
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
              dispatch(showPopup({ name: 'isLoading', value: false }));
            } catch (error) {
              console.log(error);
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message || 'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({ setFieldValue, handleSubmit, errors, touched, values }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    title="Market & Business Type"
                    onCallback={() => goBack()}
                  />
                  <Layout.Title
                    title={`${isEdit ? `Edit` : `Add`} Market & Business Type`}>
                    <View style={styles.row}>
                      <Button
                        type="default"
                        rounded
                        buttonStyle={{ width: 120, marginRight: 10 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{ fontSize: 14, letterSpacing: 0.5 }}>
                            CANCEL
                          </SociollaText>
                        }
                        onPress={() => goBack()}
                      />
                      <Button
                        type="primary"
                        rounded
                        disabled={isViewOnly}
                        buttonStyle={{ width: 120 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            SAVE
                          </SociollaText>
                        }
                        onPress={handleSubmit}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded>
                <ScrollContainer
                  style={{
                    ...(isWeb ? {} : { height: windowHeight * 0.8 }),
                  }}>
                  <Layout.FormWrapper
                    style={{
                      width: '100%',
                      paddingHorizontal: 15,
                      paddingTop: 20,
                      flexDirection: isWeb ? 'row' : 'column',
                      justifyContent: 'space-between',
                      ...(isWeb
                        ? {
                            paddingHorizontal: 20,
                            marginBottom: 15,
                          }
                        : {}),
                    }}>
                    <View
                      style={{ width: isWeb ? 'calc(50% - 15px)' : '100%' }}>
                      <Layout.FormGroup>
                        <Input
                          label="Code"
                          value={values.market_type}
                          isRequired
                          placeholder="Code"
                          hasError={
                            errors.market_type && touched.market_type
                              ? errors.market_type
                              : null
                          }
                          onChangeText={(val) => {
                            let finalValue;
                            if (val) {
                              finalValue = val.toUpperCase();
                            }
                            setFieldValue('market_type', finalValue);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          label="Business Type"
                          value={values.business_type}
                          isRequired
                          placeholder="Business Type"
                          hasError={
                            errors.business_type && touched.business_type
                              ? errors.business_type
                              : null
                          }
                          onChangeText={(val) => {
                            let finalValue;
                            if (val) {
                              finalValue = val.toUpperCase();
                            }
                            setFieldValue('business_type', finalValue);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          label="Business Type Description"
                          value={values.business_type_description}
                          isRequired
                          placeholder="Business Type"
                          hasError={
                            errors.business_type_description &&
                            touched.business_type_description
                              ? errors.business_type_description
                              : null
                          }
                          onChangeText={(val) => {
                            let finalValue;
                            if (val) {
                              finalValue = val.toUpperCase();
                            }
                            setFieldValue(
                              'business_type_description',
                              finalValue,
                            );
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaSelectBox
                          isTransparent={true}
                          label="Active/Inactive"
                          options={[
                            {
                              label: 'Active',
                              value: true,
                            },
                            {
                              label: 'In Active',
                              value: false,
                            },
                          ]}
                          name="is_active"
                          trackBy="value"
                          labelKey="label"
                          isRequired
                          value={values.is_active}
                          onChange={(val) => {
                            setFieldValue('is_active', val);
                          }}
                        />
                      </Layout.FormGroup>
                    </View>
                    <View
                      style={{ width: isWeb ? 'calc(50% - 15px)' : '100%' }}>
                      <Layout.FormGroup>
                        <PaymentTermsSearch
                          label="Payment Terms"
                          value={values.payment_terms}
                          isRequired
                          hasError={
                            errors.payment_terms && touched.payment_terms
                              ? errors.payment_terms
                              : null
                          }
                          onChange={(val) => {
                            setFieldValue('payment_terms', val);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          label="Limit Amount"
                          value={values.limit_amount}
                          isRequired
                          isNumeric={true}
                          placeholder="Limit Amount"
                          hasError={
                            errors.limit_amount && touched.limit_amount
                              ? errors.limit_amount
                              : null
                          }
                          onChangeText={(val) => {
                            setFieldValue('limit_amount', val);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <CustomerClusterSearch
                          label="Cluster"
                          value={values.cluster}
                          isRequired
                          hasError={
                            errors.cluster && touched.cluster
                              ? errors.cluster
                              : null
                          }
                          onChange={(val) => {
                            setFieldValue('cluster', val);
                          }}
                        />
                      </Layout.FormGroup>
                    </View>
                  </Layout.FormWrapper>
                  {isEdit ? (
                    <>
                      <TitleSubSection
                        title="PRODUCT"
                        disabled={loading || isViewOnly}
                        button={
                          isWeb
                            ? false
                            : {
                                label: 'Add Product',
                                callback: () =>
                                  dispatch(
                                    showPopup({
                                      name: 'marketCreateOption',
                                      value: true,
                                    }),
                                  ),
                              }
                        }
                        customButton={
                          isWeb ? (
                            <View style={{ flexDirection: 'row', gap: 10 }}>
                              <Button
                                type="outline-primary"
                                rounded
                                buttonStyle={{ width: 120 }}
                                title={
                                  <SociollaText
                                    type="medium"
                                    style={{
                                      fontSize: 14,
                                      color: Colors.blue1,
                                    }}>
                                    Export
                                  </SociollaText>
                                }
                                onPress={onExportAssortment}
                              />
                              <Button
                                type="default"
                                rounded
                                disabled={isViewOnly}
                                buttonStyle={{ width: 150 }}
                                title={
                                  <SociollaText
                                    type="bold"
                                    style={{
                                      fontSize: 14,
                                      letterSpacing: 0.5,
                                    }}>
                                    Add Product
                                  </SociollaText>
                                }
                                onPress={() =>
                                  dispatch(
                                    showPopup({
                                      name: 'marketCreateOption',
                                      value: true,
                                    }),
                                  )
                                }
                              />
                            </View>
                          ) : (
                            false
                          )
                        }
                      />
                      <View
                        style={[
                          styles.tabContainer,
                          { marginHorizontal: isWeb ? 20 : 15 },
                        ]}>
                        <TouchableOpacity
                          style={[
                            styles.tab,
                            !isWeb && { flex: 1, alignItems: 'center' },
                            activeProductTab === 'premium' && styles.activeTab,
                          ]}
                          onPress={() => {
                            setActiveProductTab('premium');
                            getMarketTypeProduct(
                              detailData?._id,
                              props?.route?.params?.businessId,
                              '',
                              0,
                              true,
                            );
                          }}>
                          <SociollaText
                            type={
                              activeProductTab === 'premium'
                                ? 'medium'
                                : undefined
                            }
                            style={{
                              fontSize: 14,
                              color:
                                activeProductTab === 'premium'
                                  ? Colors.black3
                                  : Colors.grey2,
                            }}>
                            Premium
                          </SociollaText>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.tab,
                            !isWeb && { flex: 1, alignItems: 'center' },
                            activeProductTab === 'non_premium' &&
                              styles.activeTab,
                          ]}
                          onPress={() => {
                            setActiveProductTab('non_premium');
                            getMarketTypeProduct(
                              detailData?._id,
                              props?.route?.params?.businessId,
                              '',
                              0,
                              false,
                            );
                          }}>
                          <SociollaText
                            type={
                              activeProductTab === 'non_premium'
                                ? 'medium'
                                : undefined
                            }
                            style={{
                              fontSize: 14,
                              color:
                                activeProductTab === 'non_premium'
                                  ? Colors.black3
                                  : Colors.grey2,
                            }}>
                            Non Premium
                          </SociollaText>
                        </TouchableOpacity>
                      </View>
                      <Layout.FormWrapper
                        style={{
                          paddingVertical: 10,
                          paddingHorizontal: isWeb ? 20 : 15,
                          ...(!products?.length
                            ? { justifyContent: 'center' }
                            : {}),
                        }}>
                        {!isWeb ? (
                          <View style={{ width: '100%', paddingBottom: 10 }}>
                            <SearchTable
                              testID="market-business-type-product-search"
                              value={isSearch ? searchQuery : ''}
                              width="100%"
                              withBorder={true}
                              searchPlaceholder="Search by Name, Brand, Reference, EAN"
                              onSearch={(value) =>
                                getMarketTypeProduct(
                                  detailData?._id,
                                  props?.route?.params?.businessId,
                                  value,
                                  0,
                                  activeProductTab === 'premium',
                                )
                              }
                            />
                          </View>
                        ) : (
                          false
                        )}
                        {loading ? (
                          <Layout.Loader />
                        ) : isWeb ? (
                          <Table
                            structures={
                              isViewOnly
                                ? viewOnlyTableStructure
                                : tableStructure
                            }
                            data={products}
                            withFetch={false}
                            hasNoHide
                            showFilter={false}
                            showPagination={false}
                            withoutShadow
                            totalData={products.length}
                            showSearch={true}
                            searchBorder={true}
                            searchPlaceholder="Search by Name, Brand, Reference, EAN"
                            searchKey={['name', 'reference']}
                            onSearch={(value) =>
                              getMarketTypeProduct(
                                detailData?._id,
                                props?.route?.params?.businessId,
                                value,
                                0,
                                activeProductTab === 'premium',
                              )
                            }
                            onMapAction={(val) => onMapAction(val)}
                          />
                        ) : products?.length ? (
                          products.map((item, idx) => (
                            <ProductItem
                              data={item}
                              onRemove={() => onRemoveProduct(item)}
                              showRemove={!isViewOnly}
                              isDetail
                              withoutPrice
                              key={idx}
                            />
                          ))
                        ) : (
                          <View
                            style={{
                              alignItems: 'center',
                              paddingVertical: 20,
                            }}>
                            <SociollaText style={{ color: Colors.grey2 }}>
                              No product found
                            </SociollaText>
                          </View>
                        )}
                        {loadingMore ? (
                          <View style={[styles.row, styles.buttonWrapper]}>
                            <Layout.Loader />
                          </View>
                        ) : !loadingMore &&
                          products?.length >= limit &&
                          skip <= products?.length ? (
                          <View style={[styles.row, styles.buttonWrapper]}>
                            <Button
                              rounded
                              type="primary"
                              buttonStyle={{ width: 150 }}
                              title={
                                <SociollaText
                                  type="bold"
                                  style={{
                                    fontSize: 13,
                                    letterSpacing: 0.5,
                                    color: Colors.white,
                                  }}>
                                  Load More
                                </SociollaText>
                              }
                              onPress={() =>
                                getMarketTypeProduct(
                                  detailData?._id,
                                  props?.route?.params?.businessId,
                                  isSearch ? searchQuery : '',
                                  skip + limit,
                                  activeProductTab === 'premium',
                                )
                              }
                            />
                          </View>
                        ) : (
                          false
                        )}
                      </Layout.FormWrapper>
                    </>
                  ) : (
                    false
                  )}
                </ScrollContainer>
              </Layout.Wrapper>
              {!isWeb ? (
                <View style={buttonFloating}>
                  <Button
                    rounded
                    disabled={isViewOnly}
                    buttonStyle={{ width: '100%' }}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        SAVE
                      </SociollaText>
                    }
                    onPress={handleSubmit}
                  />
                </View>
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
      <CreateNewOption
        {...props}
        show={isShowOption}
        isPremium={activeProductTab === 'premium'}
        onCallback={(val) => onSelectProduct(val)}
        onCallbackBulk={(val) => onSelectProduct(val, true)}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  tabContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey7,
    marginBottom: 5,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginBottom: -1,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: Colors.blue1,
  },
  buttonWrapper: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
  },
});

export default MarketBusinessTypeScreen;
